import { Box, CircularProgress, CircularProgressProps, Grid, Typography } from '@mui/material';
import { memo } from 'react';
import { makeStyles } from 'tss-react/mui';
import useTeceoStyles from '../useTeceoStyles';
import { formatPercentNumberString } from '../utils/formatters';
import useWidth from './Hooks/useWidth';

type CircularProgressWithLabelProps = {
  type?: 'default' | 'thick';
  onlyIcon?: boolean;
  value: number;
  customText?: string;
} & CircularProgressProps;

const useStyles = makeStyles()((theme) => ({
  bottom: {
    color: theme.palette.grey[200],
  },
  top: {
    position: 'absolute',
    left: 0,
  },
  thickTop: {
    marginLeft: -19,
  },
  circle: {
    strokeLinecap: 'round',
  },
}));

const CircularProgressWithLabel = ({
  onlyIcon,
  customText,
  type = 'default',
  ...otherProps
}: CircularProgressWithLabelProps) => {
  const { classes, cx } = useStyles();
  const { classes: teceoClasses } = useTeceoStyles();
  const { smallWidth } = useWidth();

  if (type === 'thick') {
    return (
      <Grid
        container
        spacing={1}
        sx={{
          alignItems: 'center',
        }}
      >
        {!onlyIcon && customText && (
          <Grid>
            <Typography color="primary" className={cx(teceoClasses.boldText)}>
              {customText}
            </Typography>
          </Grid>
        )}
        <Grid style={{ display: 'flex', alignItems: 'center' }}>
          <CircularProgress
            variant="determinate"
            className={classes.bottom}
            size={20}
            thickness={14}
            {...otherProps}
            value={100}
          />
          <CircularProgress
            variant="determinate"
            className={classes.thickTop}
            size={18.6}
            thickness={14}
            {...otherProps}
          />
        </Grid>
      </Grid>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
      }}
    >
      <CircularProgress
        variant="determinate"
        className={classes.bottom}
        size={smallWidth ? 40 : 44}
        {...otherProps}
        value={100}
      />
      <CircularProgress
        variant="determinate"
        className={classes.top}
        size={smallWidth ? 40 : 44}
        {...otherProps}
      />
      {!onlyIcon && (
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="body2"
            component="div"
            color="primary"
            className={cx(teceoClasses.boldText, teceoClasses.secondaryTypography)}
          >
            {formatPercentNumberString(Math.round(otherProps.value))}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

/** @deprecated Use `components/v2/ui/Progress` instead. */
export default memo(CircularProgressWithLabel);
